import { ChangeEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/stores/hooks";
import {
  setQuestionsGrouped,
  // setUpdateArticulated,
  setUpdateObservation,
  setUpdatePercentage,
  setUpdateRequirement,
  setUpdateQuestionObservation,
  setCheckAuditDataForChanges,
  setCheckAuditRequirementDataForChanges,
} from "@/stores/features/auditSlice";
import { auditService } from "@/stores/services/auditService";
import {
  AuditQuestions,
  AuditQuestionsGroup,
  Compliance,
} from "@/interfaces/Audit";
import {
  ConfigProvider,
  Input,
  Segmented,
  Table,
  TableColumnsType,
  TableProps,
} from "antd";
import { COMPLIANCE_LEVEL, COMPLIANCE_LEVEL_CONFIG } from "../utils/constants";
import { useSearchParams } from "react-router-dom";
import { decryptId } from "@/utils/utilsMethods";
import { skipToken } from "@reduxjs/toolkit/query";

interface Props {
  companyId: number;
}
interface DataType extends AuditQuestionsGroup {
  key: React.Key;
}

/**
 * Funcion constructora para determinar que opciones
 * de nivel de cumplimiento se deben mostrar a aca paso
 * @param complianceOptions Array de las opciones de nivel de cumplimiento
 * @param showNa Boolean del paso que determina si mostrar el nivel de cumplimiento NO_APLICA o no
 * @returns Array de objs para específico para renderizar en el select options de Ant design
 */
const buildComplianceOptions = (
  complianceOptions: Compliance[] | undefined,
): { label: string; value: number; disabled?: boolean }[] => {
  if (complianceOptions && complianceOptions.length > 0) {
    return complianceOptions.map((opt) => ({
      label:
        (COMPLIANCE_LEVEL_CONFIG as Record<number, { label: string }>)[opt.id]
          ?.label ?? COMPLIANCE_LEVEL_CONFIG[2].label,
      value: opt.id,
      disabled: opt.id === COMPLIANCE_LEVEL.CUMPLE_PARCIALMENTE,
    }));
  }

  return [];
};

/**
 * Funcion constructora para determinar que opciones
 * de nivel de cumplimiento se deben mostrar a aca pregunta
 * @param complianceOptions Array de las opciones de nivel de cumplimiento
 * @param showNa Boolean del paso que determina si mostrar el nivel de cumplimiento NO_APLICA o no
 * @returns Array de objs para específico para renderizar en el select options de Ant design
 */
const buildQuestionsComplianceOptions = (
  complianceOptions: Compliance[] | undefined,
): { label: string; value: number }[] => {
  if (complianceOptions && complianceOptions.length > 0) {
    return complianceOptions.map((opt) => ({
      label:
        (COMPLIANCE_LEVEL_CONFIG as Record<number, { label: string }>)[opt.id]
          ?.label ?? COMPLIANCE_LEVEL_CONFIG[2].label,
      value: opt.id,
    }));
  }

  return [];
};

export default function AuditDataTable({ companyId }: Props) {
  const dispatch = useAppDispatch();
  // const stepsLenght = useAppSelector((state) => state.util.stepLenght);
  const [searchParams] = useSearchParams();
  const auditParam = searchParams.get("audit");
  const auditId = auditParam ? parseInt(decryptId(auditParam)) : undefined;

  const {
    data: auditQuestionsByCompany,
    refetch,
    isLoading,
  } = auditService.useFindQuestionsByCompanysizeGroupedQuery(
    auditId && auditId > 0 ? { companyId, auditId } : skipToken,
  );

  useEffect(() => {
    refetch();
  }, [companyId]);
  const questionsGrouped = useAppSelector(
    (state) => state.audit.questionsGrouped,
  );

  useEffect(() => {
    if (auditQuestionsByCompany) {
      // Agrupar preguntas por step
      dispatch(setQuestionsGrouped(auditQuestionsByCompany));
    }
  }, [auditQuestionsByCompany, dispatch]);

  // useEffect(() => {
  //   if (auditQuestionsByCompany) {
  //     const transformData = (data: any[]): AuditQuestionsGroup[] => {
  //       if (!data || data.length === 0) {
  //         return [];
  //       }
  //       // Simple check to see if data is in the new format (has 'steps') or old format
  //       if (!data[0] || !data[0].steps) {
  //         return data as AuditQuestionsGroup[];
  //       }

  //       const flattenedAndGrouped = data.flatMap((cycle) =>
  //         cycle.steps.map((step: any) => {
  //           const requirementName =
  //             step.questions[0]?.requirement_name || `Paso ${step.step}`;
  //           const requirementId =
  //             step.questions[0]?.requirement_id || step.step;

  //           const questions: AuditQuestions[] = step.requirements.map(
  //             (q: any) => ({
  //               ...q,
  //               requirement_detail: {
  //                 // Constructing the expected nested object
  //                 id: requirementId,
  //                 name: requirementName,
  //                 step: step.step,
  //                 cycle: step.cycle,
  //               },
  //               variable_value: q.variable_value,
  //               requirement: requirementId,
  //               audit_type: 0, // default
  //               audit_type_detail: { id: 0, name: "" }, // default
  //             })
  //           );

  //           const group: AuditQuestionsGroup = {
  //             id: requirementId,
  //             step: step.step,
  //             cycle: cycle.cycle,
  //             requirement_name: requirementName,
  //             observation: "", // default
  //             compliance: { id: 2, name: "No Cumple" }, // default
  //             questions: questions,
  //           };
  //           return group;
  //         })
  //       );
  //       return flattenedAndGrouped;
  //     };

  //     const transformedData = transformData(auditQuestionsByCompany);
  //     dispatch(setQuestionsGrouped(transformedData));
  //   }
  // }, [auditQuestionsByCompany, dispatch]);

  // useEffect(() => {
  //   refetch();
  // }, [dispatch]);

  const auditData = useAppSelector((state) => state.audit.auditData);

  // Nuevo acceso a Redux para recuperar la auditoria del contexto
  const audit = useAppSelector((state) => state.audit.audit);

  /**
   * Nueva constante para recuperar el modo de la auditoria de Redux,
   * para hacer un condicional rendering en la zona de las columnas
   * expandibles para mejorar la UI de las observaciones de las preguntas
   */
  const isRigorous = audit?.audit_mode === "rigorous";

  const [selectedOption, setSelectedOption] = useState<{
    [step: number]: number;
  }>({});
  const [questionOption, setQuestionOption] = useState<{
    [qId: number]: number;
  }>({});
  const [_, setArticuledOption] = useState<{
    [qId: number]: boolean;
  }>({});
  // const [articuledOption, setArticuledOption] = useState<{
  //   [qId: number]: boolean;
  // }>({});

  const [observation, setObservation] = useState<{
    [reqId: number]: string | null;
  }>({});

  /**
   * Nuevo estado local para manejar las observaciones
   * de las preguntas, estas observaciones solo se podran
   * ver y editar en auditorias que tienen un modo en
   * "rigorous" o Auditoria como se muestra en la UI
   * Este estado tendra las observaciones de la auditoria
   * que se encuentre en el contexto de Redux.
   */
  const [questionObservations, setQuestionObservations] = useState<{
    [questionId: number]: string;
  }>({});

  const handleObservationChange = (
    e: ChangeEvent<HTMLTextAreaElement>,
    req_id: number,
  ) => {
    setObservation((prev) => ({
      ...prev,
      [req_id]: e.target.value,
    }));
    dispatch(
      setUpdateObservation({
        reqId: req_id,
        observation: observation[req_id],
      }),
    );
    // Aquí podrías manejar la actualización del record.observation en el estado global o realizar otras acciones
  };

  /**
   * Nueva funcion manejadora para las observaciones de preguntas
   * esta funcion mantiene actualizdo el estado local del componente
   * de las observaciones de las preguntas
   * @param questionId Number => ID de la pregunta que se usa para buscarla en las observaciones del estado local
   * @param value String => Nuevo valor de la observacion de la pregunta
   */
  const handleQuestionObservationChange = (
    questionId: number,
    value: string,
  ) => {
    // const normalized = value.trim() || "SIN OBSERVACIONES";
    setQuestionObservations((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    dispatch(
      setUpdateQuestionObservation({
        questionId,
        observation: value,
      }),
    );
    // dispatch(
    //  setUpdateQuestionObservation({
    //    questionId,
    //    observation: normalized,
    //  }),
    // );
  };

  /**
   * Nueva funcion manejadora para las observaciones de preguntas
   * esta funcion mantiene actualizdo el estado de la UI local con el estado de Redux
   * @param questionId Number => ID de la pregunta que se usa para buscarla en el Redux para actualizarla
   * @param value String => Nuevo valor de la observacion de la pregunta, si no existe, se deja como "SIN OBSERVACIONES"
   */
  const handleQuestionObservationBlur = (questionId: number, value: string) => {
    const normalized = value.trim() || "SIN OBSERVACIONES";

    setQuestionObservations((prev) => ({
      ...prev,
      [questionId]: normalized,
    }));

    dispatch(
      setUpdateQuestionObservation({
        questionId,
        observation: normalized,
      }),
    );
  };

  useEffect(() => {
    // Se limpian los datos huerfanos antes de inicializar
    dispatch(setCheckAuditDataForChanges());
    dispatch(setCheckAuditRequirementDataForChanges());

    // Reenderizado por defecto
    if (questionsGrouped.length > 0) {
      questionsGrouped.forEach((group) => {
        dispatch(
          setUpdateRequirement({
            compliance: group.compliance.id,
            observation: observation[group.id] ?? null,
            requirementId: group.id,
          }),
        );
        group.questions.forEach((question) => {
          dispatch(
            setUpdatePercentage({
              questionId: question.id,
              compliance: auditId
                ? (question.compliance_detail?.id ?? COMPLIANCE_LEVEL.NO_CUMPLE)
                : COMPLIANCE_LEVEL.NO_CUMPLE,
            }),
          );
          setQuestionOption((prev) => ({
            ...prev,
            [question.id]: auditId
              ? (question.compliance_detail?.id ?? COMPLIANCE_LEVEL.NO_CUMPLE)
              : COMPLIANCE_LEVEL.NO_CUMPLE,
          }));
          setArticuledOption((prev) => ({
            ...prev,
            [question.id]: true,
          }));
          // Se inicializan las observaciones de las preguntas
          // Se busca cada una de las preguntas de la respuesta de la API y se extrae la observacion
          const existingObservation =
            auditData.find((d) => d.question === question.id)?.observation ??
            question.observation ??
            null;

          // Si existe la observacion se asigna de lo contrario se deja "SIN OBSERVACIONES" por default
          const initialObservation = existingObservation?.trim()
            ? existingObservation.trim()
            : "SIN OBSERVACIONES";

          // Se actualiza el valor de la descripcion de las preguntas de Redux, para tener coherencia entre lo que llega de la API con lo que se mantiene en el contexto global
          setQuestionObservations((prev) => ({
            ...prev,
            [question.id]: initialObservation,
          }));
          dispatch(
            setUpdateQuestionObservation({
              questionId: question.id,
              observation: initialObservation,
            }),
          );
        });
        setSelectedOption((prev) => ({
          ...prev,
          [group.step]: group.compliance.id,
        }));
        setObservation((prev) => ({
          ...prev,
          [group.id]: group.observation,
        }));
      });
    }
  }, [questionsGrouped, dispatch]);

  const handleComplianceChange = (
    value: number,
    questionId: number,
    step: number,
    reqId: number,
  ) => {
    dispatch(
      setUpdatePercentage({
        compliance: value,
        questionId,
      }),
    );
    // Actualiza el estado local para la opción seleccionada
    setQuestionOption((prev) => {
      const newQuestionOptions = { ...prev, [questionId]: value };

      // Agrupa las preguntas por paso
      const grouped = questionsGrouped.filter((group) => group.step === step);

      // Obtén todas las respuestas para el paso específico
      const allQuestionValues = grouped.flatMap((group) =>
        group.questions.map((question) => newQuestionOptions[question.id]),
      );

      // Determina el valor que se debe establecer en selectedOption
      const newSelectedOption = allQuestionValues.includes(3)
        ? COMPLIANCE_LEVEL.CUMPLE_PARCIALMENTE // Si hay al menos un valor 3
        : allQuestionValues.every((val) => val === COMPLIANCE_LEVEL.NO_CUMPLE)
          ? COMPLIANCE_LEVEL.NO_CUMPLE // Si todos los valores son 2
          : allQuestionValues.every(
                (val) =>
                  val === COMPLIANCE_LEVEL.CUMPLE ||
                  val == COMPLIANCE_LEVEL.NO_APLICA,
              )
            ? COMPLIANCE_LEVEL.CUMPLE // Si todos los valores son 1
            : allQuestionValues.every(
                  (val) =>
                    val === COMPLIANCE_LEVEL.CUMPLE ||
                    val === COMPLIANCE_LEVEL.NO_CUMPLE ||
                    val === COMPLIANCE_LEVEL.CUMPLE_PARCIALMENTE,
                )
              ? COMPLIANCE_LEVEL.CUMPLE_PARCIALMENTE
              : allQuestionValues.every(
                    (val) => val === COMPLIANCE_LEVEL.NO_APLICA,
                  )
                ? COMPLIANCE_LEVEL.NO_APLICA
                : COMPLIANCE_LEVEL.CUMPLE_PARCIALMENTE;

      // Actualiza selectedOption basado en la verificación
      setSelectedOption((prev) => ({
        ...prev,
        [step]: newSelectedOption,
      }));
      dispatch(
        setUpdateRequirement({
          compliance: newSelectedOption,
          observation: observation[reqId] ?? null,
          requirementId: reqId,
        }),
      );

      return newQuestionOptions;
    });
  };

  // const handleArticulatedChange = (value: boolean, questionId: number) => {
  //   dispatch(setUpdateArticulated({ questionId, isArticulated: value }));
  //   setArticuledOption((prev) => ({
  //     ...prev,
  //     [questionId]: value,
  //   }));
  // };

  const handleGeneralChange = (value: number, step: number, req_id: number) => {
    if (questionsGrouped.length > 0) {
      dispatch(
        setUpdateRequirement({
          compliance: value,
          observation: observation[req_id],
          requirementId: req_id,
        }),
      );
      questionsGrouped.forEach((group) => {
        group.questions.forEach((question) => {
          if (question.requirement_detail.step === step) {
            dispatch(
              setUpdatePercentage({
                questionId: question.id,
                compliance: value,
              }),
            );
            setQuestionOption((prev) => ({ ...prev, [question.id]: value }));
          }
        });
        setSelectedOption((prev) => ({ ...prev, [step]: value }));
      });
    }
  };
  const defaultColors = {
    itemActiveBg: "#007bff",
    itemColor: "#000",
    itemHoverBg: "#f0f0f0",
    itemHoverColor: "#333333",
    itemSelectedBg: "#ffffff",
    itemSelectedColor: "#fff",
    trackBg: "#f5f5f5",
  };
  const conditionalColors: any = {
    1: {
      itemActiveBg: "#28a745",
      itemColor: "#BABABA",
      itemSelectedBg: "#28a745",
      itemSelectedColor: "#fff",
    },
    2: {
      itemActiveBg: "#dc3545",
      itemColor: "#BABABA",
      itemSelectedBg: "#dc3545",
      itemSelectedColor: "#fff",
    },
    3: {
      itemActiveBg: "#ffc107",
      itemColor: "#BABABA",
      itemSelectedBg: "#ffc107",
      itemSelectedColor: "#fff",
    },
    4: {
      itemActiveBg: "#17a2b8",
      itemColor: "#BABABA",
      itemSelectedBg: "#17a2b8",
      itemSelectedColor: "#fff",
    },
    true: {
      itemActiveBg: "#17a2b8",
      itemColor: "#BABABA",
      itemSelectedBg: "#17a2b8",
      itemSelectedColor: "#fff",
    },
    false: {
      itemActiveBg: "#17a2b8",
      itemColor: "#BABABA",
      itemSelectedBg: "#000",
      itemSelectedColor: "#fff",
    },
  };

  const columns: TableColumnsType<AuditQuestionsGroup> = [
    {
      title: "PASO PESV",
      dataIndex: "step",
      key: "step",
      render: (text: number) => (
        <span>
          <span className="font-extrabold">{text}</span>
        </span>
      ),
    },
    {
      title: "FASE",
      dataIndex: "cycle",
      key: "cycle",
    },
    {
      title: "Requerimiento",
      dataIndex: "requirement_name",
      key: "requirement_name",
    },
    {
      title: "Nivel de Cumplimiento",
      render: (_, record) => {
        const colors =
          conditionalColors[selectedOption[record.step]] || defaultColors;
        return (
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  ...colors,
                },
              },
            }}
          >
            <Segmented
              key={record.step}
              size="middle"
              options={buildComplianceOptions(record.compliance_options)}
              value={selectedOption[record.step]}
              defaultValue={COMPLIANCE_LEVEL.NO_CUMPLE}
              onChange={(value) =>
                handleGeneralChange(value, record.step, record.id)
              }
            />
          </ConfigProvider>
        );
      },
    },
    {
      title: "Observacion",
      render: (_, record) => {
        return (
          <Input.TextArea
            value={observation[record.id] ?? ""}
            rows={1}
            className={
              selectedOption[record.step] !== 4
                ? "hidden transition-all"
                : "w-60 transition-all"
            }
            placeholder="En caso de que no aplique este paso"
            disabled={selectedOption[record.step] !== 4}
            autoSize={{ minRows: 1, maxRows: 6 }}
            onChange={(e) => handleObservationChange(e, record.id)}
          />
        );
      },
    },
  ];

  /**
   * Constante para extraer la configuracion de la columna de observaciones
   * de las preguntas para poder realizar un condicional rendering
   * dependiendo del modo de la auditoria del Redux. De esta forma
   * si la auditoria tiene un modo distinto a "rigorous" esta columna
   * de observaciones no se mostrara ni se tendra en cuenta para
   * el renderizado.
   * * */
  const questionObservationColumn = {
    title: "Observación",
    width: "35%",
    render: (_: unknown, question: AuditQuestions) => {
      return (
        <Input.TextArea
          value={questionObservations[question.id] ?? "SIN OBSERVACIONES"}
          rows={1}
          autoSize={{ minRows: 1, maxRows: 6 }}
          placeholder="Observación de la pregunta"
          onChange={(e) =>
            handleQuestionObservationChange(question.id, e.target.value)
          }
          onBlur={(e) =>
            handleQuestionObservationBlur(question.id, e.target.value)
          }
        />
      );
    },
  };

  const expandableConfig: TableProps<AuditQuestionsGroup>["expandable"] = {
    expandedRowRender: (group) => (
      <Table<AuditQuestions>
        columns={[
          {
            title: "Criterio de Verificación",
            dataIndex: "name",
            key: "name",
            width: isRigorous ? "35%" : "70%",
          },
          {
            title: "Variable Value",
            dataIndex: "variable_value",
            key: "variable_value",
            width: "10%",
            render: (text) => `${text}%`,
          },
          {
            title: "Nivel de Cumplimiento",
            width: "20%",
            render: (_, question) => {
              const compliance =
                auditData.find((d) => d.question === question.id)?.compliance ||
                COMPLIANCE_LEVEL.NO_CUMPLE;

              const colors =
                conditionalColors[questionOption[question.id]] || defaultColors;
              return (
                <ConfigProvider
                  theme={{
                    components: {
                      Segmented: {
                        ...colors,
                      },
                    },
                  }}
                >
                  <Segmented
                    key={question.id}
                    size="small"
                    options={buildQuestionsComplianceOptions(
                      question.compliance_options,
                    )}
                    value={compliance}
                    defaultValue={COMPLIANCE_LEVEL.NO_CUMPLE}
                    onChange={(value) =>
                      handleComplianceChange(
                        value,
                        question.id,
                        question.requirement_detail.step,
                        group.id,
                      )
                    }
                  />
                </ConfigProvider>
              );
            },
          },
          /**
           * Nueva columna en la seccion de los datos expandibles
           * Esta nueva columna manejara las obaservaciones de las preguntas
           * solo de aquellas auditorias que tienen unicamente un modo "rigorous"
           * Adicionalmente se muestra con un condicional rendering para mejorar
           * el rendimiento
           * */
          ...(isRigorous ? [questionObservationColumn] : []),

          // {
          //   title: "Esta articulado?",
          //   render: (_, question, index) => {
          //     const articulated = diagnosisData.find(
          //       (d) => d.question === question.id
          //     )?.is_articuled;

          //     const colors =
          //       conditionalColors[articuledOption[question.id] as any] ||
          //       defaultColors;
          //     return index === group.questions.length - 1 ? (
          //       <ConfigProvider
          //         theme={{
          //           components: {
          //             Segmented: {
          //               ...colors,
          //             },
          //           },
          //         }}
          //       >
          //         <Segmented
          //           key={index}
          //           size="small"
          //           options={[
          //             {
          //               label: "SI",
          //               value: true,
          //             },
          //             {
          //               label: "NO",
          //               value: false,
          //             },
          //           ]}
          //           value={articulated}
          //           defaultValue={true}
          //           onChange={(value) =>
          //             handleArticulatedChange(value, question.id)
          //           }
          //         />
          //       </ConfigProvider>
          //     ) : null;
          //   },
          // },
        ]}
        dataSource={group.questions}
        pagination={false}
        rowKey="id"
        rowClassName={(_, index) =>
          index === group.questions.length - 1 ? "font-bold" : ""
        }
        rowHoverable={false}
      />
    ),
    rowExpandable: (record) => record.questions.length > 0,
  };

  const dataSource: DataType[] = questionsGrouped.map((group) => ({
    ...group,
    key: group.id,
  }));
  return (
    <Table<AuditQuestionsGroup>
      columns={columns}
      loading={isLoading}
      dataSource={dataSource}
      pagination={false} // Desactiva la paginación si no la necesitas
      expandable={expandableConfig}
      rowClassName={() => "bg-slate-100"}
      className="w-full overflow-auto"
    />
  );
}
